/**
 * Fifi AI Scheduling Co-pilot — Persistent Panel
 *
 * Lives alongside the strip board as a persistent side panel showing:
 * - Live schedule stats (total days, pages/day, unscheduled, actor holds)
 * - Chat interface connected to /api/projects/{id}/ai-chat with scheduling context
 * - Quick actions (conflict summary, export hints)
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Wand2, Send, X, Film, MapPin, Clock, AlertTriangle, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compute live stats from schedule + scenes
function computeStats(days, breakdownById) {
  const scheduledIds = new Set(days.flatMap(d => d.scene_ids || []));
  const totalScenes = Object.keys(breakdownById).length;
  const scheduled = scheduledIds.size;
  const unscheduled = totalScenes - scheduled;

  // Pages per day
  const pageCounts = days.map(d =>
    (d.scene_ids || []).reduce((sum, sid) => sum + (breakdownById[sid]?.page_count_eighths || 0), 0) / 8
  );
  const avgPages = pageCounts.length > 0 ? pageCounts.reduce((a, b) => a + b, 0) / pageCounts.length : 0;
  const maxPages = pageCounts.length > 0 ? Math.max(...pageCounts) : 0;

  // Actor appearances per day
  const actorDays = {};
  days.forEach(d => {
    const cast = new Set();
    (d.scene_ids || []).forEach(sid => {
      (breakdownById[sid]?.characters_in_scene || []).forEach(a => cast.add(a));
    });
    cast.forEach(a => {
      actorDays[a] = (actorDays[a] || 0) + 1;
    });
  });
  const topActors = Object.entries(actorDays)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Location moves (days with multiple distinct locations)
  const companyMoves = days.filter(d => {
    const locs = new Set(
      (d.scene_ids || []).map(sid => breakdownById[sid]?.location_name || '').filter(Boolean)
    );
    return locs.size > 1;
  }).length;

  return { scheduled, unscheduled, totalScenes, avgPages, maxPages, topActors, companyMoves, totalDays: days.length };
}

const QUICK_PROMPTS = [
  'Which actors have the most scenes? Can we reduce their hold days?',
  'Which scenes can be moved to reduce company moves?',
  'Flag any scenes that violate Kerala FEFKA 10-hour day rule',
  'What is the optimal order to shoot the night exterior scenes?',
  'Which day has the most cast? Suggest splitting it',
  'Are there any dawn/dusk scenes that can be clustered?',
];

export default function FifiSchedulePanel({ projectId, token, days, breakdownById, conflicts, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [statsExpanded, setStatsExpanded] = useState(true);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const stats = useMemo(() => computeStats(days, breakdownById), [days, breakdownById]);

  useEffect(() => {
    // Welcome message
    if (messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Hi! I'm **Fifi AI**, your scheduling co-pilot.\n\nI have full access to your breakdown, cast, locations, and current schedule. Ask me anything:\n\n• *"Which scenes have both Radhika and Marcus?"*\n• *"How many night exteriors are at the harbour?"*\n• *"Flag any FEFKA turnaround violations"*`,
      }]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    // Build scheduling context
    const scheduleContext = `
[SCHEDULE CONTEXT]
Total shoot days: ${stats.totalDays}
Scheduled scenes: ${stats.scheduled}/${stats.totalScenes}
Average pages/day: ${stats.avgPages.toFixed(1)}
Max pages in a day: ${stats.maxPages.toFixed(1)}
Company moves (days with 2+ locations): ${stats.companyMoves}
Active conflicts: ${conflicts.length}
Top actors by shoot days: ${stats.topActors.map(([n, d]) => `${n} (${d} days)`).join(', ')}
Kerala/Malayalam industry note: FEFKA union standard is 10-hour day max, 12-hour turnaround. Typical page count 8-10 pages/day.
`;

    const history = messages.map(m => ({ role: m.role, content: m.content }));

    try {
      const { data } = await axios.post(
        `${API}/projects/${projectId}/ai-chat`,
        {
          message: msg + scheduleContext,
          history,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply, applied: data.applied || [] }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Sorry, something went wrong: ${err.response?.data?.detail || err.message}`,
      }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const renderLine = (line, li) => {
    const parts = line.split(/(\*[^*]+\*)/g);
    return (
      <p key={li} className={li > 0 ? 'mt-1' : ''}>
        {parts.map((part, pi) =>
          part.startsWith('*') && part.endsWith('*') && part.length > 2
            ? <strong key={pi}>{part.slice(1, -1)}</strong>
            : part
        )}
      </p>
    );
  };

  return (
    <div className="flex flex-col h-full bg-[#0a0a0a] border-l border-[#2a2a2a]" data-testid="fifi-schedule-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2a2a2a] shrink-0">
        <div className="flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-[#e8c547]" />
          <span className="font-['DM_Serif_Display'] text-sm text-[#f0f0f0]">Fifi AI</span>
          <span className="text-[9px] text-[#555] bg-[#1a1a1a] border border-[#2a2a2a] px-1.5 py-0.5 rounded">Scheduling Co-pilot</span>
        </div>
        <button onClick={onClose} className="text-[#555] hover:text-[#f0f0f0] transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Live Stats */}
      <div className="border-b border-[#1e1e1e] shrink-0">
        <button
          className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-[#111] transition-colors"
          onClick={() => setStatsExpanded(e => !e)}
        >
          <span className="text-xs font-semibold text-[#a0a0a0]">Live Stats</span>
          {statsExpanded ? <ChevronUp className="h-3 w-3 text-[#555]" /> : <ChevronDown className="h-3 w-3 text-[#555]" />}
        </button>

        {statsExpanded && (
          <div className="px-4 pb-3 grid grid-cols-2 gap-2">
            <StatCard icon={Film} label="Shoot Days" value={stats.totalDays} />
            <StatCard icon={TrendingUp} label="Scheduled" value={`${stats.scheduled}/${stats.totalScenes}`} />
            <StatCard icon={Clock} label="Avg Pages/Day" value={stats.avgPages.toFixed(1)} warn={stats.avgPages > 10} />
            <StatCard icon={MapPin} label="Company Moves" value={stats.companyMoves} warn={stats.companyMoves > 3} />
            {conflicts.length > 0 && (
              <div className="col-span-2 flex items-center gap-1.5 px-2 py-1.5 rounded bg-red-500/10 border border-red-500/20">
                <AlertTriangle className="h-3 w-3 text-red-400 shrink-0" />
                <span className="text-xs text-red-400">{conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} detected</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[88%] rounded-xl px-3 py-2 text-xs ${
              msg.role === 'user'
                ? 'bg-[#e8c547] text-black rounded-br-sm'
                : 'bg-[#1a1a1a] border border-[#2a2a2a] text-[#e0e0e0] rounded-bl-sm'
            }`}>
              {msg.content.split('\n').map((line, li) => renderLine(line, li))}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl rounded-bl-sm px-3 py-2">
              <div className="flex gap-1">
                {[0, 150, 300].map(d => (
                  <span key={d} className="h-1.5 w-1.5 bg-[#e8c547] rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Quick prompts */}
      <div className="px-3 py-2 border-t border-[#1e1e1e] flex gap-1.5 overflow-x-auto shrink-0">
        {QUICK_PROMPTS.slice(0, 3).map((q, i) => (
          <button
            key={i}
            onClick={() => send(q)}
            disabled={loading}
            className="text-[9px] px-2 py-1 rounded bg-[#1a1a1a] border border-[#2a2a2a] text-[#666] hover:text-[#f0f0f0] hover:border-[#e8c547]/40 whitespace-nowrap transition-colors disabled:opacity-50 shrink-0"
          >
            {q.slice(0, 30)}…
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="px-3 pb-3 pt-1 shrink-0">
        <div className="flex gap-2 items-end">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about your schedule…"
            rows={2}
            className="flex-1 bg-[#1a1a1a] border border-[#2a2a2a] text-[#f0f0f0] text-xs px-3 py-2 rounded-lg resize-none outline-none focus:border-[#e8c547]/50 placeholder:text-[#333]"
            data-testid="fifi-schedule-input"
          />
          <button
            onClick={() => send()}
            disabled={loading || !input.trim()}
            className="h-9 w-9 rounded-lg bg-[#e8c547] hover:bg-[#d6b539] text-black flex items-center justify-center transition-all disabled:opacity-40"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, warn }) {
  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded border ${warn ? 'bg-yellow-500/5 border-yellow-500/20' : 'bg-[#1a1a1a] border-[#2a2a2a]'}`}>
      <Icon className={`h-3 w-3 shrink-0 ${warn ? 'text-yellow-400' : 'text-[#555]'}`} />
      <div>
        <div className={`text-xs font-semibold ${warn ? 'text-yellow-400' : 'text-[#f0f0f0]'}`}>{value}</div>
        <div className="text-[9px] text-[#555]">{label}</div>
      </div>
    </div>
  );
}
